'use client';

import { useState } from 'react';
import Image, { type ImageProps } from 'next/image';
import { ImageOff } from 'lucide-react';
import { cn } from '../utils/cn';

interface S3ImageProps extends Omit<ImageProps, 'unoptimized'> {
  src: string;
  showSkeleton?: boolean;
  fadeDuration?: number;
  unoptimized?: boolean;
}

/**
 * Image component wrapper for S3 images.
 */
export function S3Image({
  src,
  className,
  showSkeleton = true,
  fadeDuration = 300,
  unoptimized = true,
  onLoad,
  onError,
  ...props
}: S3ImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setIsLoaded(true);
    setHasError(false);
    onLoad?.(e);
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setHasError(true);
    setIsLoaded(true);
    console.error('S3Image load error:', src);
    onError?.(e);
  };

  return (
    <div className="relative h-full w-full">
      {showSkeleton && !isLoaded && (
        <div
          className="absolute inset-0 animate-pulse rounded-inherit bg-secondary/50"
          style={{ borderRadius: 'inherit' }}
        />
      )}

      {hasError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-secondary/50 text-muted-foreground">
          <ImageOff className="h-8 w-8" />
          <span className="text-xs">Не удалось загрузить</span>
        </div>
      )}

      {!hasError && (
        <Image
          src={src}
          unoptimized={unoptimized}
          className={cn(
            'transition-opacity',
            isLoaded ? 'opacity-100' : 'opacity-0',
            className
          )}
          style={{
            transitionDuration: `${fadeDuration}ms`,
            ...props.style,
          }}
          onLoad={handleLoad}
          onError={handleError}
          {...props}
        />
      )}
    </div>
  );
}
